import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
	fetchSeries,
	fetchCategories,
	fetchProviders,
	fetchInstitutionsList,
	fetchTagsList
} from 'store/actions';

// Import defaultValues
import { defaultFieldValues } from './fields/input_fields';

// Import translation
import { useTranslation } from 'react-i18next';

// Import utilities
import { submitForm } from 'components/utilities/form';

// Validate
import validate from './validation';

// Import components
import { FormTemplate } from 'components/common/templates';
import { FormButtons } from 'components/common/buttons';
import Fields from './fields/Fields';
import ProductizationNote from 'components/utilities/info/ProductizationNote';

// Import helpers
import {
	vodCategoriesOptions,
	convertDataToNames,
	convertToIds
} from 'components/helpers/genres_helpers/helpers';
import {
	getInstitutionName,
	convertInstitutionsToValuePairs
} from 'components/helpers/institutions_helpers/helpers';
import { convertProvidersToId } from 'components/views/vod/helpers/helpers';
import { convertDurationTime } from 'components/helpers/convert';

// Import variables
import { DURATION_CONVERT_TYPES } from 'components/helpers/variables';

// Import hooks
import useAudioLocation from 'hooks/useAudioLocation';

const { HMS_TO_SECONDS, SECONDS_TO_HMS } = DURATION_CONVERT_TYPES;

const SeriesForm = ({
	history,
	fetchSeries,
	fetchCategories,
	fetchProviders,
	error,
	isLoaded,
	initialValues,
	genresList,
	providersList,
	paymentsModelsList,
	fetchInstitutionsList,
	institutionsList,
	match: {
		params: { id }
	},
	fetchTagsList,
	tagsList
}) => {
	useEffect(() => {
		if (id) localStorage.setItem('series', id);
		// Fetch categories with default params to feed genres combobox
		fetchCategories(vodCategoriesOptions, null, null);
		fetchProviders();
		// Fetch tags list
		fetchTagsList();
		// eslint-disable-next-line
	}, []);

	const { t } = useTranslation();

	const { isAudio } = useAudioLocation();

	const {
		metadata,
		genres,
		subtype,
		products_group_uuid,
		is_audio,
		tags
	} = initialValues;
	const { actors, writers, directors, producers, duration, rating } = metadata;

	const handleOnSubmit = ({ isEdit, itemID }) => async (values) => {
		const { genres, subtype, products_group_uuid, tags, metadata } = values;

		// selected genres mapped to array of ids
		const genresId = convertToIds(genresList, genres);

		// selected tags mapped to array of ids
		const tagsId = convertToIds(tagsList, tags);

		// convert duration from sec to min
		const durationInSeconds = convertDurationTime({
			duration: metadata.duration,
			type: HMS_TO_SECONDS
		});

		// resources to submit
		const resources = {
			...values,
			metadata: {
				...metadata,
				duration: durationInSeconds,
				sub_category: metadata.sub_category,
				rating: parseInt(metadata.rating, 10),
				additional_metadata: metadata.additional_metadata || [],
				title_org: metadata.title
			},
			genres: genresId,
			tags: tagsId,
			products_group_uuid: products_group_uuid || null,
			subtype: subtype || defaultFieldValues.subtype
		};

		const api = subtype === 'series' ? `vod/${subtype}` : 'vod';

		// Submit the form with field values
		return await submitForm({
			history,
			isEdit,
			itemID,
			resources,
			api,
			redirectPath: 'vod',
			apiSlug: 'update'
		});
	};

	// array of genres names that will be passed to multi-select
	// both initial values and data for combobox
	const genresNames = convertDataToNames(genres);
	const genresListNames = convertDataToNames(genresList);

	// arrays of provider id's passed to suggestion combobox for form fields
	const providersId = convertProvidersToId(providersList);

	// array of tags names that will be passed to multi-select
	// both initial values and data for combobox
	const tagsNames = convertDataToNames(tags || []);
	const tagsListNames = convertDataToNames(tagsList);

	// convert duration from sec to hms
	const durationInHms = convertDurationTime({
		duration,
		type: SECONDS_TO_HMS
	});

	// form initial values
	// get institution name based on institution id
	const productGroupName = getInstitutionName(
		products_group_uuid,
		institutionsList
	);
	const institutionsNames = convertInstitutionsToValuePairs(institutionsList);

	const formInitialValues = {
		...defaultFieldValues,
		...initialValues,
		metadata: {
			...defaultFieldValues.metadata,
			...metadata,
			duration: durationInHms,
			category: metadata?.category || '',
			rating: rating ? '' + rating : '0', //fast way to convert value to string
			additional_data: {
				...defaultFieldValues.metadata.additional_data,
				...metadata.additional_data
			},
			additional_metadata: {
				...defaultFieldValues.metadata.additional_metadata,
				...metadata.additional_metadata
			}
		},
		genres: genresNames,
		tags: tagsNames,
		products_group_uuid: productGroupName?.value,
		is_audio: is_audio || isAudio
	};

	return (
		// Dispatch fetchSeries actions in series_form
		<FormTemplate
			fetchFormResources={[fetchInstitutionsList]}
			fetchResourceToEdit={fetchSeries}
			error={error}
			isLoaded={isLoaded}
		>
			{({ isEdit, itemID, checkedPermissions, decorators }) => (
				<>
					<ProductizationNote />
					<Form
						initialValues={formInitialValues}
						validate={validate}
						decorators={decorators}
						onSubmit={handleOnSubmit({ isEdit, itemID })}
						render={({
							handleSubmit,
							submitting,
							values,
							form: { change }
						}) => (
							<form onSubmit={handleSubmit}>
								<Fields
									currentValues={values}
									isEdit={isEdit}
									submitting={submitting}
									changeFieldValue={change}
									actors={actors}
									writers={writers}
									directors={directors}
									producers={producers}
									genres={genresListNames}
									subtype={subtype}
									providersId={providersId}
									paymentsModelsList={paymentsModelsList}
									institutions={institutionsNames}
									checkedPermissions={checkedPermissions}
									tags={tagsListNames}
								/>
								<FormButtons
									isButtonDisabled={submitting || error}
									path="vod"
									buttonText={t('common:buttons.submit')}
								/>
							</form>
						)}
					/>
				</>
			)}
		</FormTemplate>
	);
};

SeriesForm.propTypes = {
	history: PropTypes.object.isRequired,
	initialValues: PropTypes.object.isRequired,
	fetchSeries: PropTypes.func.isRequired,
	fetchCategories: PropTypes.func.isRequired,
	fetchProviders: PropTypes.func.isRequired,
	error: PropTypes.bool.isRequired,
	isLoaded: PropTypes.bool.isRequired,
	genresList: PropTypes.array.isRequired,
	providersList: PropTypes.array.isRequired,
	paymentsModelsList: PropTypes.array.isRequired,
	institutionsList: PropTypes.array.isRequired,
	fetchInstitutionsList: PropTypes.func.isRequired,
	match: PropTypes.shape({
		params: PropTypes.shape({ id: PropTypes.string })
	}),
	fetchTagsList: PropTypes.func.isRequired
};

const mapStateToProps = ({
	vod: { series_form },
	categories: {
		table: { data }
	},
	providers,
	dictionary
}) => ({
	initialValues: series_form.edit,
	error: series_form.error,
	isLoaded: series_form.isLoaded,
	genresList: data,
	providersList: providers.table.data,
	paymentsModelsList: dictionary.paymentModels,
	institutionsList: dictionary.institutionsList,
	tagsList: dictionary.tagsList
});

export default compose(
	connect(mapStateToProps, {
		fetchSeries,
		fetchCategories,
		fetchProviders,
		fetchInstitutionsList,
		fetchTagsList
	}),
	withRouter
)(SeriesForm);
